import React, { FC } from 'react';
import { Provider } from 'react-redux';
import { QueryClientProvider } from 'react-query';
import { Router, Route, Switch } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { IntercomProvider } from 'react-use-intercom';
import { ReactQueryDevtools } from 'react-query/devtools';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { queryClient } from '@investown/fe/api-sdk';
import { ErrorBoundary, AnalyticsProvider, createAnalyticsService, createGTMService } from '@investown/fe/common-utils';

import { theme } from 'styles/theme';
import { history, store } from 'appRedux/store';
import AppContainer from 'containers/App/index';
import 'assets/vendors/style';
import 'styles/wieldy.less';
import FeatureFlagsProvider from 'featureFlags/FeatureFlagsProvider';
import ErrorPage from 'components/Errors/ErrorPage';
import { initSentry } from 'util/sentry';
import PageViewsLogger from 'util/gtm/PageViewsLogger';
import { createBloomreachWebAnalyticsService } from 'util/bloomreach';

const analyticsService = createAnalyticsService([createGTMService(), createBloomreachWebAnalyticsService()]);

const App: FC = () => {
  const intercomAppId = process.env.REACT_APP_INTERCOM_APP_ID;
  if (!intercomAppId) {
    throw new Error('Missing REACT_APP_INTERCOM_APP_ID env variable');
  }

  analyticsService.init();
  initSentry(); // init Sentry before render (outside useEffect) in case of early errors

  const configCatSdkKey = process.env.REACT_APP_CONFIG_CAT_SDK_KEY;
  if (!configCatSdkKey || configCatSdkKey === '') {
    throw new Error('Missing REACT_APP_CONFIG_CAT_SDK_KEY env variable');
  }

  const recaptchaKey = process.env.REACT_APP_RECAPTCHA_KEY;

  if (!recaptchaKey) {
    throw new Error('Missing REACT_APP_RECAPTCHA_KEY env variable');
  }

  return (
    <ErrorBoundary fallbackComponent={<ErrorPage />}>
      <AnalyticsProvider analyticsService={analyticsService}>
        <IntercomProvider appId={intercomAppId} autoBoot>
          <ThemeProvider theme={theme}>
            <Provider store={store}>
              <FeatureFlagsProvider sdkKey={configCatSdkKey}>
                <QueryClientProvider client={queryClient}>
                  <ReactQueryDevtools initialIsOpen={false} />
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-ignore fix by updating react-router-dom package */}
                  <Router history={history}>
                    <Switch>
                      <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}>
                        <PageViewsLogger />
                        <Route path="/" component={AppContainer} />
                      </GoogleReCaptchaProvider>
                    </Switch>
                  </Router>
                </QueryClientProvider>
              </FeatureFlagsProvider>
            </Provider>
          </ThemeProvider>
        </IntercomProvider>
      </AnalyticsProvider>
    </ErrorBoundary>
  );
};

export default App;
